import React, { useState } from "react";
import { ATSItem } from "../../../api/ATS";
import LoadingSpinner from "../../../shared-components/LoadingSpinner";
import { Modal } from "../../../shared-components/Modal";
import Button, { ButtonColorTypes } from "../../../shared-components/Button";
import { useDeleteATSIntegration } from "../../../api/ATSApi";
import { useApplicationToast } from "../../../webcontent/ApplicationToastProvider";
import { CheckIcon, BellAlertIcon } from "@heroicons/react/24/outline";
import ReactTooltip from "react-tooltip";

interface IntegrationType {
  id: string;
  name: string;
  logo: string;
  status: "active" | "coming_soon";
}

interface IntegrationCardProps {
  integration: IntegrationType;
  isConnected: boolean;
  connectionDetails?: ATSItem;
  onConnect: () => void;
  isLoading: boolean;
  onShowAlert?: (
    message: string,
    type: "warning" | "success" | "error"
  ) => void;
}

const IntegrationCard: React.FC<IntegrationCardProps> = ({
  integration,
  isConnected,
  connectionDetails,
  onConnect,
  isLoading,
  onShowAlert,
}) => {
  const isComingSoon = integration.status === "coming_soon";
  const [isManageModalOpen, setIsManageModalOpen] = useState(false);
  const { showLargeToast } = useApplicationToast();
  const { mutate: deleteIntegration, isLoading: isDeletingIntegration } =
    useDeleteATSIntegration();

  const handleOpenSetupLink = () => {
    if (connectionDetails?.link) {
      window.open(connectionDetails.link, "_blank");
      setTimeout(() => {
        if (onShowAlert) {
          onShowAlert(
            "It may take a few minutes for your connection to display as complete. You can refresh the page to check the status.",
            "warning"
          );
        } else {
          showLargeToast?.({
            title: "Connection in progress",
            message:
              "It may take a few minutes for your connection to display as complete. You can refresh the page to check the status.",
            icon: (
              <div className="bg-blue-100 text-blue-600 rounded-full p-2">
                <BellAlertIcon className="w-6" />
              </div>
            ),
          });
        }
      }, 500);
    }
  };

  const handleOpenManageModal = () => {
    setIsManageModalOpen(true);
  };

  const handleCloseManageModal = () => {
    setIsManageModalOpen(false);
  };

  const handleDisconnect = () => {
    if (!connectionDetails) return;

    deleteIntegration(connectionDetails.external_id, {
      onSuccess: () => {
        showLargeToast?.({
          title: "Success!",
          message: `${integration.name} integration disconnected successfully`,
          icon: (
            <div className="bg-green-100 text-white rounded-full p-2">
              <CheckIcon className="w-6" />
            </div>
          ),
        });
        handleCloseManageModal();
        onConnect(); // Refresh the integrations list
      },
      onError: (error) => {
        showLargeToast?.({
          title: "Error",
          message: "Failed to disconnect integration",
          icon: (
            <div className="bg-red-100 text-white rounded-full p-2">
              <BellAlertIcon className="w-6" />
            </div>
          ),
        });
      },
    });
  };

  return (
    <>
      <div className="bg-white border rounded-lg shadow-sm overflow-hidden hover:shadow-md transition-shadow">
        <div className="p-6">
          <div className="flex justify-between items-start mb-4">
            <div className="h-12 w-12 flex items-center justify-center">
              <img
                src={integration.logo}
                alt={`${integration.name} logo`}
                className="max-h-full max-w-full"
              />
            </div>
            {isConnected ? (
              connectionDetails?.status.toLowerCase() === "pending" ? (
                <span className="text-xs px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full">
                  Pending
                </span>
              ) : (
                <span className="text-xs px-2 py-1 bg-green-100 text-green-800 rounded-full">
                  Connected
                </span>
              )
            ) : isComingSoon ? (
              <span className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded-full">
                Coming Soon
              </span>
            ) : (
              <span className="text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded-full">
                Available
              </span>
            )}
          </div>

          <h3 className="text-lg font-semibold mb-2">{integration.name}</h3>

          {isLoading ? (
            <div className="flex justify-center">
              <LoadingSpinner />
            </div>
          ) : isConnected && connectionDetails ? (
            <div className="space-y-2">
              {connectionDetails.integration_id && (
                <div className="text-sm">
                  <span className="font-medium text-gray-700">ID:</span>{" "}
                  <span className="font-mono text-xs">
                    {connectionDetails.integration_id}
                  </span>
                </div>
              )}

              {connectionDetails.status.toLowerCase() === "pending" ? (
                <button
                  className="w-full mt-2 text-sm text-blue-600 border border-blue-200 rounded-md py-2 px-4 hover:bg-blue-50 transition-colors"
                  onClick={handleOpenSetupLink}
                >
                  Setup Connection
                </button>
              ) : (
                <button
                  className="w-full mt-2 text-sm text-red-600 border border-red-200 rounded-md py-2 px-4 hover:bg-red-50 transition-colors"
                  onClick={handleOpenManageModal}
                >
                  Manage Connection
                </button>
              )}
            </div>
          ) : isComingSoon ? (
            <>
              <span
                data-tip="Contact your account manager to find out more."
                data-for={`tooltip-${integration.id}`}
              >
                <button
                  className="w-full bg-gray-100 text-gray-500 py-2 px-4 rounded-md cursor-not-allowed"
                  disabled
                >
                  Coming Soon
                </button>
              </span>
              <ReactTooltip id={`tooltip-${integration.id}`} effect="solid" />
            </>
          ) : (
            <button
              className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 transition-colors"
              onClick={onConnect}
            >
              Connect
            </button>
          )}
        </div>
      </div>

      <Modal
        open={isManageModalOpen}
        width="w-1/2"
        onClose={handleCloseManageModal}
      >
        <div className="p-6">
          <div className="flex items-center space-x-4 mb-6">
            <div className="h-12 w-12 flex items-center justify-center">
              <img
                src={integration.logo}
                alt={`${integration.name} logo`}
                className="max-h-full max-w-full"
              />
            </div>
            <h2 className="text-xl font-semibold">
              Manage {integration.name} Connection
            </h2>
          </div>

          <div className="mb-6">
            <h3 className="text-lg font-medium mb-2">Connection Details</h3>
            <div className="bg-gray-50 p-4 rounded-md">
              <div
                className={
                  connectionDetails?.integration_id
                    ? "grid grid-cols-2 gap-4"
                    : ""
                }
              >
                {connectionDetails?.integration_id && (
                  <div>
                    <p className="text-sm text-gray-500">Integration ID</p>
                    <p className="font-mono text-sm">
                      {connectionDetails?.integration_id}
                    </p>
                  </div>
                )}
                <div>
                  <p className="text-sm text-gray-500">Status</p>
                  <p
                    className={`${
                      connectionDetails?.status.toLowerCase() === "active"
                        ? "text-green-600 font-medium"
                        : "text-yellow-600 font-medium"
                    }`}
                  >
                    {connectionDetails?.status.toLowerCase() === "active"
                      ? "Connected"
                      : "Pending Setup"}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="border-t pt-6">
            <h3 className="text-lg font-medium mb-4 text-red-600">
              Danger Zone
            </h3>
            <p className="text-sm text-gray-700 mb-4">
              Disconnecting this integration will remove the connection between
              your systems. This action cannot be undone.
            </p>

            <div className="flex justify-end space-x-4">
              <Button
                text="Cancel"
                colorType={ButtonColorTypes.custom}
                className="bg-white border border-gray-300 text-gray-700 rounded-md"
                onClick={handleCloseManageModal}
              />
              <Button
                text="Disconnect Integration"
                colorType={ButtonColorTypes.custom}
                className="bg-red-600 text-white rounded-md"
                onClick={handleDisconnect}
                disabled={isDeletingIntegration}
              />
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default IntegrationCard;
